import React, { FC, useCallback, useState } from "react";
import { Box, Button, CircularProgress, Chip, Menu, MenuItem, Tooltip } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { UseGkvBillsReturn } from "../types/interface";
import GkvBillCard from "./GkvBillCard";
import GkvHeaderActions from "./GkvHeaderActions";
import GkvBillSummary from "./GkvBillSummary";
import GkvBulkActionToolbar from "./GkvBulkActionToolbar";
import SpotlightSearch from "../../common/components/SpotlightSearch";
import { DeleteConfirmationDialog } from "../../common/exports";
import { useGkvBillSave } from "../hooks/useGkvBillSave";
import { useApi } from "../../../../../../api/ApiProvider";
import { WaitingBackdrop } from "../../../../Base/components/BackDrop";
import notificationStore from "../../../../../../stores/Notification/notificationStore";
import { DirectBillingSingleRxResponseDto } from "../../../../../../api-client";
import { colors } from "../styles/tokens";

/** Props for the bill workspace (left panel) */
export interface GkvBillWorkspaceProps {
  gkvBills: UseGkvBillsReturn;
  account: string;
  userName: string;
  userId: string;
  onRowClick: (rx: any) => void;
  onRxDelete: (rxId: string) => void;
  onValidationFilter: (remainingRxIds: string[]) => void;
  onBillsSaved?: (createdIds: string[]) => void;
  handleFullScreenDialogClose: () => void;
}

/**
 * Left-panel workspace for the GKV New Bill flow.
 *
 * Renders:
 * 1. Sticky header with actions (load, add Rx, filter, save)
 * 2. Bill summary chips
 * 3. List of bill cards grouped by payer
 * 4. SpotlightSearch dialog for adding a single Rx
 *
 * Components for sections 1-4 will be added in Phases 3-5.
 */
const GkvBillWorkspace: FC<GkvBillWorkspaceProps> = ({
  gkvBills,
  account,
  userName,
  userId,
  onRowClick,
  onRxDelete,
  onValidationFilter,
  onBillsSaved,
  handleFullScreenDialogClose,
}) => {
  const {
    bills,
    validationMap,
    isLoading,
    isValidating,
    addRx,
    removeRx,
    removeAllByPayer,
    filterValid,
    refetch,
    validationProgress,
  } = gkvBills;

  const { transaction } = useApi();

  // Notes state keyed by payerIK
  const [notes, setNotes] = useState<Record<string, string>>({});

  // Save hook
  const { isSaving, saveBills } = useGkvBillSave({ bills, notes, account, userId });

  // Row-level context menu state
  const [selectedRxId, setSelectedRxId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRx, setMenuRx] = useState<any>(null);
  const [isSpotlightOpen, setIsSpotlightOpen] = useState(false);
  const [isAddingRx, setIsAddingRx] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // Stepper state
  const [activeStep, setActiveStep] = useState(0);

  // Bulk selection state
  const [selectedRxIds, setSelectedRxIds] = useState<Set<string>>(new Set());

  const toggleRxSelection = useCallback((rxId: string) => {
    setSelectedRxIds((prev) => {
      const next = new Set(prev);
      if (next.has(rxId)) next.delete(rxId);
      else next.add(rxId);
      return next;
    });
  }, []);

  const clearRxSelection = useCallback(() => setSelectedRxIds(new Set()), []);

  /** Bulk-delete all selected Rxs */
  const handleBulkDelete = useCallback(() => {
    selectedRxIds.forEach((id) => {
      removeRx(id);
      onRxDelete(id);
    });
    clearRxSelection();
  }, [selectedRxIds, removeRx, onRxDelete, clearRxSelection]);

  /** Add a single Rx from SpotlightSearch */
  const handleAddSingleRx = useCallback(
    async (rxId: string) => {
      try {
        setIsAddingRx(true);
        const response = (await transaction.loadSingleGkvBillRx(
          rxId,
        )) as DirectBillingSingleRxResponseDto;
        const wasAdded = addRx(response);
        if (!wasAdded) {
          notificationStore.showMessage("Dieses Rezept ist bereits auf der Liste", "warning");
        } else {
          notificationStore.showMessage("Rezept hinzugefügt", "success");
        }
      } catch (error: any) {
        const message = error.message || "Rezept konnte nicht hinzugefügt werden";
        notificationStore.showMessage(message, "warning");
      } finally {
        setIsAddingRx(false);
      }
    },
    [transaction, gkvBills],
  );

  /** Save all bills */
  const handleSave = useCallback(async () => {
    const ids = await saveBills();
    if (ids) {
      setActiveStep(1);
      if (onBillsSaved) {
        onBillsSaved(ids);
      } else {
        handleFullScreenDialogClose();
      }
    }
  }, [saveBills, onBillsSaved, handleFullScreenDialogClose]);

  /** Filter to valid-only and notify parent about removed Rxs */
  const handleFilterValid = useCallback(() => {
    filterValid();
    // After filtering, collect remaining Rx IDs so parent can clear selection if needed
    const remainingIds = bills.flatMap((b) =>
      b.rxs
        .filter((rx) => {
          const v = validationMap[rx.id];
          return v && Object.values(v).every((val) => val.validity === "Valid");
        })
        .map((rx) => rx.id),
    );
    onValidationFilter(remainingIds);
  }, [bills, validationMap, filterValid, onValidationFilter]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, rx: any) => {
    setAnchorEl(event.currentTarget);
    setMenuRx(rx);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  /** Remove the Rx selected in the context menu */
  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
    setAnchorEl(null);
  };

  /** Confirm delete from dialog */
  const handleDeleteConfirm = () => {
    if (menuRx) {
      removeRx(menuRx.id);
      onRxDelete(menuRx.id);
    }
    setOpenDeleteDialog(false);
    setMenuRx(null);
  };

  /** Handle row click — notify parent + track selection */
  const handleRowClick = (rx: any) => {
    setSelectedRxId(rx.id);
    onRowClick(rx);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "95vh" }}>
      <WaitingBackdrop isWorking={isSaving || isAddingRx || isReloading} message="Bitte warten" />
      {/* Sticky header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          background: colors.darkBlue, //"linear-gradient(180deg, #375e86 0%, #2a5881 100%)",
          borderRadius: "0 0 12px 12px",
          px: 1.5,
          py: 0.75,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}
      >
        <GkvHeaderActions
          activeStep={activeStep}
          hasBills={bills.length > 0}
          isValidating={isValidating}
          validationProgress={validationProgress}
          onLoadAll={async () => {
            setIsReloading(true);
            await refetch();
            setIsReloading(false);
          }}
          onAddRx={() => setIsSpotlightOpen(true)}
          onFilterValid={handleFilterValid}
        />
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ flex: 1 }}>
            <GkvBillSummary bills={bills} validationMap={validationMap} />
          </Box>
          {bills.length > 0 && (
            <Tooltip
              title={isValidating ? "Validierung läuft..." : "Rechnungnummer Erstellen"}
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  size="small"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                  onClick={handleSave}
                  disabled={isValidating}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: "#6ab187",
                    color: "#fff",
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.5,
                    fontSize: "0.8rem",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: "#5aa97f",
                    },
                    "&:disabled": {
                      backgroundColor: "#546e7a",
                      color: "#90a4ae",
                    },
                  }}
                >
                  Weiter
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
        {/* Bulk action toolbar */}
        <GkvBulkActionToolbar
          selectedCount={selectedRxIds.size}
          onDelete={handleBulkDelete}
          onClear={clearRxSelection}
        />
      </Box>

      {/* Loading indicator */}
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            py: 4,
          }}
        >
          <CircularProgress color="primary" />
          <Chip label="Daten werden geladen, bitte warte..." />
        </Box>
      )}

      {/* Bill cards grouped by payer */}
      {!isLoading &&
        bills.map((bill) => (
          <Box key={bill.payerIK} sx={{ mb: 1, mt: 1 }}>
            <GkvBillCard
              bill={bill}
              validationMap={validationMap}
              account={account}
              userName={userName}
              selectedRxId={selectedRxId}
              selectedRxIds={selectedRxIds}
              onRowClick={handleRowClick}
              onMenuOpen={handleMenuOpen}
              onRemoveAll={removeAllByPayer}
              onToggleRxSelection={toggleRxSelection}
              notes={notes[bill.payerIK] || ""}
              onNotesChange={(val) => setNotes((prev) => ({ ...prev, [bill.payerIK]: val }))}
            />
          </Box>
        ))}

      {!isLoading && bills.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
          Keine Rezepte gefunden.
        </Box>
      )}

      {/* SpotlightSearch for adding single Rx */}
      <SpotlightSearch
        open={isSpotlightOpen}
        onClose={() => setIsSpotlightOpen(false)}
        onRxSelect={(rx) => {
          handleAddSingleRx(rx.id);
          setIsSpotlightOpen(false);
        }}
      />

      {/* Delete confirmation dialog */}
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onCancel={() => {
          setOpenDeleteDialog(false);
          setMenuRx(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Aus der Liste entfernen?"
      />

      {/* Row context menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleDeleteClick}>Entfernen</MenuItem>
      </Menu>
    </Box>
  );
};

export default GkvBillWorkspace;
